import { create } from 'zustand';
import { Deal, AcceptDealPayload, RejectDealPayload } from '../types';
import { dealService } from '../services/dealService';

interface DealStore {
  deals: Deal[];
  currentDeal: Deal | null;
  isLoading: boolean;
  error: string | null;

  fetchDeals: (params?: { status?: string; page?: number }) => Promise<void>;
  fetchDealDetail: (id: string) => Promise<void>;
  acceptDeal: (dealId: string, payload: AcceptDealPayload) => Promise<Deal>;
  rejectDeal: (dealId: string, payload: RejectDealPayload) => Promise<Deal>;
  updateDeal: (deal: Deal) => void;
  clearError: () => void;
}

export const useDealStore = create<DealStore>((set, get) => ({
  deals: [],
  currentDeal: null,
  isLoading: false,
  error: null,

  fetchDeals: async (params) => {
    try {
      set({ isLoading: true, error: null });
      const data = await dealService.list(params);
      set({ deals: data.results ?? [] });
    } catch (err: any) {
      set({ error: err?.message || 'Failed to fetch deals' });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchDealDetail: async (id: string) => {
    try {
      set({ isLoading: true, error: null });
      const data = await dealService.getDetail(id);
      set({ currentDeal: data });
    } catch (err: any) {
      set({ error: err?.message || 'Failed to fetch deal' });
    } finally {
      set({ isLoading: false });
    }
  },

  acceptDeal: async (dealId: string, payload: AcceptDealPayload) => {
    try {
      set({ isLoading: true, error: null });
      const deal = await dealService.accept(dealId, payload);
      get().updateDeal(deal);
      return deal;
    } catch (err: any) {
      set({ error: err?.message || 'Failed to accept deal' });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  rejectDeal: async (dealId: string, payload: RejectDealPayload) => {
    try {
      set({ isLoading: true, error: null });
      const deal = await dealService.reject(dealId, payload);
      get().updateDeal(deal);
      return deal;
    } catch (err: any) {
      set({ error: err?.message || 'Failed to reject deal' });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  updateDeal: (deal: Deal) => {
    set((state) => ({
      deals: state.deals.map((d) => (d.id === deal.id ? deal : d)),
      currentDeal: state.currentDeal?.id === deal.id ? deal : state.currentDeal,
    }));
  },

  clearError: () => set({ error: null }),
}));
